/**
 * Safe localStorage utilities for mobile environments
 * Handles Safari Private Mode and restricted localStorage access gracefully
 */

// In-memory fallback storage for environments without localStorage
let memoryStorage: { [key: string]: string } = {};

/**
 * Checks if localStorage is available and can be used
 */
export function isStorageAvailable(): boolean {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) {
      return false;
    }
    const testKey = '__psicosys_storage_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Safe wrapper for localStorage.getItem
 * Falls back to memory storage if localStorage is unavailable
 */
export function safeGetItem(key: string): string | null {
  try {
    if (isStorageAvailable()) {
      return window.localStorage.getItem(key);
    } else {
      return memoryStorage[key] || null;
    }
  } catch (e) {
    console.warn('Erro ao acessar localStorage para chave:', key, e);
    return memoryStorage[key] || null;
  }
}

/**
 * Safe wrapper for localStorage.setItem
 * Falls back to memory storage if localStorage is unavailable
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    if (isStorageAvailable()) {
      window.localStorage.setItem(key, value);
      // Also store in memory as backup
      memoryStorage[key] = value;
      return true;
    } else {
      memoryStorage[key] = value;
      return false; // Indicates fallback was used
    }
  } catch (e) {
    console.warn('Erro ao salvar no localStorage para chave:', key, e);
    memoryStorage[key] = value;
    return false;
  }
}

/**
 * Safe wrapper for localStorage.removeItem
 * Also removes from memory storage
 */
export function safeRemoveItem(key: string): void {
  try {
    if (isStorageAvailable()) {
      window.localStorage.removeItem(key);
    }
    delete memoryStorage[key];
  } catch (e) {
    console.warn('Erro ao remover do localStorage para chave:', key, e);
    delete memoryStorage[key];
  }
}

/**
 * Safe wrapper for localStorage.clear
 * Also clears memory storage
 */
export function safeClear(): void {
  try {
    if (isStorageAvailable()) {
      window.localStorage.clear();
    }
    memoryStorage = {};
  } catch (e) {
    console.warn('Erro ao limpar localStorage:', e);
    memoryStorage = {};
  }
}

/**
 * Gets all keys from storage (both localStorage and memory fallback)
 */
export function safeGetAllKeys(): string[] {
  const keys = new Set<string>();
  
  try {
    if (isStorageAvailable()) {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key) keys.add(key);
      }
    }
    
    // Add memory storage keys
    Object.keys(memoryStorage).forEach(key => keys.add(key));
    
    return Array.from(keys);
  } catch (e) {
    console.warn('Erro ao obter chaves do storage:', e);
    return Object.keys(memoryStorage);
  }
}

/**
 * Checks if we're using memory storage fallback
 */
export function isUsingMemoryFallback(): boolean {
  return !isStorageAvailable();
}

/**
 * Gets memory storage for debugging purposes
 */
export function getMemoryStorage(): { [key: string]: string } {
  return { ...memoryStorage };
}